/**
 * Outcome Row — one entry in the card's outcome list.
 *
 * Used for both binary (YES/NO with check/X glyph icons) and multi-choice
 * (contender image) variants. The whole row is the click target — clicking
 * anywhere opens the market detail page in a new tab.
 *
 * The icon slot is a child element rather than a built-in image: callers
 * supply either a colored glyph (binary) or a market thumbnail (multi-choice),
 * each rendered the same 40×40 rounded-square slot.
 */

import type { JSX, ComponentChildren } from "preact";
import { messages } from "../i18n";
import { PriceChangeDirection } from "../utils/formatters";
import { TrendingDownIcon, TrendingUpIcon } from "./material-icons";

export interface OutcomeRowProps {
  icon: ComponentChildren;
  title: string;
  volumeText: string;
  priceChangeText: string;
  priceChangeDirection: PriceChangeDirection;
  probability: number;
  onClick: () => void;
  ariaLabel?: string;
}

const interpolate = (template: string, value: string): string =>
  template.replace("{value}", value);

export function OutcomeRow({
  icon,
  title,
  volumeText,
  priceChangeText,
  priceChangeDirection,
  probability,
  onClick,
  ariaLabel,
}: OutcomeRowProps): JSX.Element {
  const m = messages();
  const TrendIcon =
    priceChangeDirection === PriceChangeDirection.Down
      ? TrendingDownIcon
      : TrendingUpIcon;

  return (
    <button
      aria-label={ariaLabel ?? title}
      className="pw:flex pw:items-center pw:gap-3 pw:w-full pw:p-0 pw:cursor-pointer pw:bg-transparent pw:text-left"
      onClick={onClick}
      type="button"
    >
      {/* Icon slot — 40×40 rounded square. Caller supplies content. The
          backplate (`bg-type-muted-foreground`) shows during image-load
          and as the muted base for the binary YES/NO outcome glyphs (which
          paint their own colored fills on top). */}
      <div className="pw:flex pw:items-center pw:justify-center pw:flex-shrink-0 pw:w-10 pw:h-10 pw:rounded-lg pw:overflow-hidden pw:bg-type-muted-foreground">
        {icon}
      </div>

      {/* Title + sub-row + probability */}
      <div className="pw:flex pw:flex-1 pw:items-center pw:justify-between pw:min-w-0 pw:gap-2">
        <div className="pw:flex pw:flex-col pw:gap-1 pw:min-w-0">
          <span className="pw:text-[1.0625rem] pw:font-medium pw:leading-[1.375rem] pw:tracking-[-0.0125em] pw:text-type-primary-foreground pw:truncate">
            {title}
          </span>
          <div className="pw:flex pw:items-center pw:gap-1.5 pw:text-[0.8125rem] pw:font-medium pw:leading-4 pw:text-static-modal-foreground-muted">
            <span className="pw:truncate">
              {interpolate(m.card.outcomeVolume, volumeText)}
            </span>
            <span className="pw:flex pw:items-center pw:gap-1 pw:flex-shrink-0">
              <TrendIcon className="pw:h-5 pw:w-5" />
              <span>
                {interpolate(m.card.outcomePriceChange, priceChangeText)}
              </span>
            </span>
          </div>
        </div>
        <span className="pw:text-2xl pw:font-medium pw:leading-8 pw:tracking-[-0.005em] pw:text-type-primary-foreground pw:flex-shrink-0">
          {probability}%
        </span>
      </div>
    </button>
  );
}
